"use client"

import * as React from "react"
import { Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { toEnDigits } from "@/lib/format"
import { domainCheckerUrl, domainRegisterUrl } from "@/lib/whmcs"

/**
 * Domain availability entry point.
 *
 * Availability itself is answered by WHMCS — this form only normalises what the
 * visitor typed and hands off. That avoids duplicating registrar logic and
 * keeps a single source of truth for pricing and stock.
 */
export function DomainSearch({
  size = "lg",
  className,
  popularTlds = ["ir", "com", "net", "org"],
}: {
  size?: "md" | "lg"
  className?: string
  popularTlds?: string[]
}) {
  const [value, setValue] = React.useState("")
  const [error, setError] = React.useState<string | null>(null)
  const inputId = React.useId()

  /** Strip protocol/path/www and normalise Persian digits before handing to WHMCS. */
  const normalize = (raw: string) =>
    toEnDigits(raw)
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/.*$/, "")
      .replace(/\s+/g, "")

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const domain = normalize(value)

    if (!domain) {
      setError("لطفاً نام دامنه مورد نظرتان را وارد کنید.")
      return
    }
    if (!/^[a-z0-9؀-ۿ][a-z0-9؀-ۿ.-]*$/i.test(domain)) {
      setError("نام دامنه فقط می‌تواند شامل حروف، عدد و خط تیره باشد.")
      return
    }

    setError(null)
    window.location.assign(
      domain.includes(".") ? domainRegisterUrl(domain) : domainCheckerUrl(domain),
    )
  }

  return (
    <div className={cn(className)}>
      <form onSubmit={onSubmit} noValidate>
        <label htmlFor={inputId} className="sr-only">
          جستجوی دامنه
        </label>

        <div
          className={cn(
            "flex flex-col gap-2.5 rounded-[var(--radius-lg)] border border-[var(--hairline-strong)] bg-[var(--surface)] p-2 shadow-md ring-1 ring-brand-500/8 sm:flex-row sm:items-center sm:p-2.5",
          )}
        >
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute inset-y-0 start-4 my-auto size-[1.15rem] text-[var(--content-subtle)]"
              aria-hidden
            />
            <input
              id={inputId}
              type="text"
              // Domains are Latin: force LTR so the caret and the TLD behave.
              dir="ltr"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder="مثال: shop.ir یا example.com"
              value={value}
              onChange={(event) => {
                setValue(event.target.value)
                if (error) setError(null)
              }}
              aria-invalid={!!error}
              aria-describedby={error ? `${inputId}-error` : undefined}
              className={cn(
                "w-full rounded-[var(--radius-lg)] bg-transparent ps-12 pe-4 text-start",
                "font-medium text-[var(--content)] placeholder:text-[var(--content-subtle)]",
                "focus:outline-none",
                size === "lg" ? "h-14 text-base" : "h-12 text-[0.9375rem]",
              )}
            />
          </div>

          <Button type="submit" size={size === "lg" ? "lg" : "md"} className="shrink-0 sm:min-w-40">
            بررسی دامنه
          </Button>
        </div>
      </form>

      {error ? (
        <p id={`${inputId}-error`} role="alert" className="mt-2.5 ps-2 text-[0.8125rem] font-medium text-danger-500">
          {error}
        </p>
      ) : null}

      {popularTlds.length ? (
        <ul className="mt-4 flex flex-wrap items-center gap-2 ps-2">
          <li className="text-[0.75rem] text-[var(--content-subtle)]">پیشنهاد:</li>
          {popularTlds.map((tld) => (
            <li key={tld}>
              <button
                type="button"
                onClick={() => {
                  const base = normalize(value).split(".")[0]
                  setValue(base ? `${base}.${tld}` : `.${tld}`)
                }}
                className={cn(
                  "ltr-nums rounded-full px-2.5 py-1 text-[0.75rem] font-semibold",
                  "bg-[var(--canvas-subtle)] text-[var(--content-muted)] ring-1 ring-[var(--hairline)]",
                  "transition-colors hover:text-brand-600 dark:hover:text-brand-300",
                )}
              >
                .{tld}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
